<template>
    <v-card
        id="dragdrop"
        elevation="1"
        flat
        :class="[
            grid !== '2cols' ? 'no-margin' : '',
            isBig ? 'flex-height' : 'fixed-height',
            'no-border'
        ]"
        :style="[disabled ? 'display:none;' : '']"
        @click.passive="setActive()"
    >
        <div ref="container" :style="setThumbnailHeightStyle">
            <expansion-panel-header
                :tooltipText="tooltipText"
                :collapsable="collapsable"
                :collapsed="dragdropCollapsed"
                :loading="anyTabsLoading"
                @click="toggleCollapse"
            >
                {{ t_title }}
            </expansion-panel-header>
            <div v-if="!dragdropCollapsed"
                ref="tab"
                id="dragdrop-nav-tabs"
                :class="[showDndSelector ? 'dragdrop-nav-tab pl-3 d-flex flex-row mr-4' : 'display-none']"
            >
                <dragdrop-tooltip-container
                    hideSlider
                    :selectedTab="tab_number"
                    :darkMode="projectPrimaryColorIsDark"
                    :elements="elementTypes"
                    @click="handleSelectedTabChange"
                />
            </div>
            <div class="dragdrop-content" ref="content" id="content" v-show="!dragdropCollapsed || grid==='3cols'" @click="collapseSettings">
                <v-tabs-items v-model="tab_number">
                    <v-tab-item eager v-if="mediapool" key="mediapool" >
                        <tab-item-mediapool :disabled="disabled"
                                            :elementLayerUsergroupFilteringFunction="isElementVisible"
                                            :elements-per-row-class="elementsPerRowClass"
                                            :height="calculatedHeights.default"
                                            :list-height="calculatedHeights.defaultListHeight"
                                            :permission-add="mediapoolAdd"
                                            :readonly="readonly"
                                            :sort-option-list="sortOptionList"
                                            @loading="loadingMediapool = $event"
                        />
                    </v-tab-item>
                    <v-tab-item eager v-if="templates" key="templates">
                        <tab-item-templates :disabled="disabled"
                                            :elementLayerUsergroupFilteringFunction="isElementVisible"
                                            :elements-per-row-class="elementsPerRowClass"
                                            :height="calculatedHeights.default"
                                            :list-height="calculatedHeights.defaultListHeight"
                                            :permission-add="templatesAdd"
                                            :readonly="readonly"
                                            :sort-option-list="sortOptionList"
                                            :ignore-id="ignoreType == 'template' ? ignoreId : undefined"
                                            @loading="loadingTemplates = $event"
                        />
                    </v-tab-item>
                    <v-tab-item eager v-if="campaigns" key="campaigns">
                        <tab-item-campaigns :disabled="disabled"
                                            :elementLayerUsergroupFilteringFunction="isElementVisible"
                                            :elements-per-row-class="elementsPerRowClass"
                                            :height="calculatedHeights.default"
                                            :list-height="calculatedHeights.defaultListHeight"
                                            :permission-add="campaignsAdd"
                                            :readonly="readonly"
                                            :sort-option-list="sortOptionList"
                                            :ignore-id="ignoreType == 'campaign' ? ignoreId : undefined"
                                            @loading="loadingCampaigns = $event"
                        />
                    </v-tab-item>
                    <v-tab-item eager v-if="elements" key="elements">
                        <v-card flat :height="calculatedHeights.default">
                            <div class="panel-body no-padding" v-if="disabled === false">
                                <div class="scrollBox" :style="`height:${calculatedHeights.defaultListHeight}px;`">
                                    <element-list classes="no-padding"
                                                  :element-class="elementsPerRowClass"
                                                  :useLegacyElements="useLegacyElements">
                                    </element-list>
                                </div>
                            </div>
                        </v-card>
                    </v-tab-item>
                    <v-tab-item eager v-if="widgets" key="widgets">
                        <v-card flat :height="calculatedHeights.default">
                            <div class="panel-body no-padding" v-if="disabled === false">
                                <div class="dragdrop-filters-container">
                                    <v-text-field
                                        v-model="search_widgets"
                                        :aria-label="t_search"
                                        :label="t_search"
                                        dense
                                        hide-details
                                        outlined
                                        style="min-width: 64px"
                                    />
                                    <filter-select
                                        v-model="sort_option_widgets"
                                        :label="t_sort_by"
                                        :items="sortOptionList"
                                        dense
                                        style="flex-basis: 60px; flex-grow: 0">
                                        <template v-slot:selection="{item}">
                                            <div class="dragdrop-sort-selection-container">
                                                <i :class="[item.text[0]]"/>
                                                <i :class="[item.text[1]]"/>
                                            </div>
                                        </template>
                                        <template v-slot:item="{active, item}">
                                            <div class="dragdrop-sort-selection-container"
                                                 :class="{'active': active}">
                                                <i :class="[item.text[0]]"/>
                                                <i :class="[item.text[1]]"/>
                                            </div>
                                        </template>
                                    </filter-select>
                                </div>
                                <div class="scrollBox" :style="'height:' + calculatedHeights.defaultListHeight + 'px;'">
                                    <widgets-list :widgets="sortedWidgetsList"
                                                  :readonly="readonly"
                                                  :small="true"
                                                  :loading="loadingWidgets"
                                                  classes="no-padding"
                                                  force-mode="tiles"
                                                  :drag-drop="true"
                                                  :elementClass="elementsPerRowClass"
                                                  :add="widgetsAdd">
                                    </widgets-list>
                                </div>
                            </div>
                        </v-card>
                    </v-tab-item>
                </v-tabs-items>
            </div>
        </div>
        <showMediaModal/>
    </v-card>
</template>

<script>
import elementList from "./designelementsList.vue";
import widgetsList from '../../administration/widgets/widgets-list.vue';
import showMediaModal from '../mediapool/show-media-modal.vue';
import {isBright} from "../../../utility";
import toaster from "../../common/toaster";
import TooltipTab from "./tooltip-tab.vue";
import DragdropTooltipContainer from "./dragdrop-tooltip-container.vue";
import PublishTooltipButton from "./publish-tooltip-button.vue";
import ExpansionPanelHeader from "./expansion-panel-header.vue";
import {validityDateToJsDate} from '../../../../utils/date';
import TabItemMediapool from './dragdrop/tabItemMediapool.vue';
import TabItemCampaigns from './dragdrop/tabItemCampaigns.vue';
import TabItemTemplates from './dragdrop/tabItemTemplates.vue';
import HeightCalculator, {TIMEOUT_DURATION} from "./HeightCalculator";

const LSKeyCurrentTab = `drag-and-drop-current-tab.${Laravel.activeUser}.${Laravel.activeProject}`;

export default {
    components: {
        ExpansionPanelHeader,
        PublishTooltipButton,
        DragdropTooltipContainer,
        TooltipTab,
        elementList,
        widgetsList,
        showMediaModal,
        TabItemCampaigns,
        TabItemMediapool,
        TabItemTemplates,
    },
    props: [
        'mediapool',
        'mediapoolAdd',
        'elements',
        'widgets',
        'widgetsAdd',
        'templates',
        'templatesAdd',
        'campaigns',
        'campaignsAdd',
        'disableCanvas',
        'grid',
        'mode',
        'startCollapsed',
        'startActive',
        'ignoreId',
        'ignoreType',
        'disabled',
        'fixed_layers_filter',
        'fixed_usergroups_filter',
        'publish_inactive',
        'useLegacyElements'
    ],
    data: function () {
        return {
            t_title: Lang.langGet("Drag & Drop"),
            t_search: Lang.langGet("Search"),
            t_mediapool: Lang.langGet("Mediapool"),
            t_elements: Lang.langChoice("Element|Elements", 2),
            t_templates: Lang.langChoice("Template|Templates", 2),
            t_campaigns: Lang.langChoice("Campaign|Campaigns", 2),
            t_widgets: Lang.langChoice("Widget|Widgets", 2),
            t_load_usergroups_failed: Lang.langGet("Failed to load usergroups from database."),
            t_load_widgets_failed: Lang.langGet("The widgets are not loaded."),
            t_load_layers_failed: Lang.langGet("Failed to load layers from database."),
            t_sort_date_asc: ["fa-regular fa-down-long", "fa-regular fa-clock fa-fw"],
            t_sort_date_des: ["fa-regular fa-up-long", "fa-regular fa-clock fa-fw"],
            t_sort_name_asc: ["fa-regular fa-down-long", "fa-regular fa-a fa-fw"],
            t_sort_name_des: ["fa-regular fa-up-long", "fa-regular fa-a fa-fw"],
            t_sort_by: Lang.langGet("Sort by"),
            t_expand: Lang.langGet("Expand"),
            t_show_less: Lang.langGet("Show less"),
            t_collapse: Lang.langGet("Collapse"),

            search_widgets: '',
            fullUsergroupList: {
                data: [],
                hierarchy: 'down'
            },
            selectedUsergroupsHierarchyConsidered: [],
            allowedLayerList: [],
            activeLayerList: [],
            fixedLayersFilter: [],
            fixedUsergroupsFilter: [],
            widgetsList: [],
            readonly: true,
            editorFrame: null,
            layoutInfo: null,
            settingsCollapsed: false,
            dragdropCollapsed:false,
            showDndSelector: true,
            active: this.startActive,
            isBig: false,
            expandHeight:0,
            setActiveBlocked: false,
            blockActiveButton: false,
            tab_number: 0,
            elementsPerRowClass: '',
            selectedLayerNr: null,
            selectedLayerIds: [],
            selectedUsergroupIds: [],
            selectedUsergroupsStrict: false,
            sort_option_widgets: 2,
            drag_drop_values: {
                top: 0,
                left: 0,
                height: 0,
                width: 0,
            },
            loadTriesWidgets: 0,
            loadTriesUsergroups: 0,
            loadTriesLayers: 0,

            loadingMediapool: true,
            loadingTemplates: true,
            loadingCampaigns: true,
            loadingWidgets: true,

            heightCalculator: new HeightCalculator(),
            calculatedHeights: {}
        }
    },
    computed: {
        //should only take into account loading if the tab is not disabled
        anyTabsLoading() {
            return (this.loadingMediapool && this.mediapool) ||
                (this.loadingTemplates && this.templates) ||
                (this.loadingCampaigns && this.campaigns) ||
                (this.loadingWidgets && this.widgets);
        },
        tooltipText() {
            return `${this.dragdropCollapsed ? this.t_expand : this.t_collapse} ${this.t_title}`
        },
        collapsable() {
            return !this.anyTabsLoading && this.grid === '2cols';
        },
        sortOptionList: function () {
            return [
                { value: 0, text: this.t_sort_name_asc },
                { value: 1, text: this.t_sort_name_des },
                { value: 2, text: this.t_sort_date_asc },
                { value: 3, text: this.t_sort_date_des },
            ];
        },
        projectPrimaryColorIsDark: function () {
            return isBright(Laravel.primary_color) === 'dark';
        },
        currentTabName() {
            return this.elementTypes[this.tab_number].key;
        },
        widgetDisabled() {
            if (this.loadingWidgets) return true;
            if (!this.widgets) return true;
            return !this.widgetsList?.length;
        },
        elementTypes() {
            let tabs = [
                {key: 'mediapool', name: this.t_mediapool, icon: 'mediapool', disabled: !this.mediapool},
                {key: 'templates', name: this.t_templates, icon: 'template',  disabled: !this.templates},
                {key: 'campaigns', name: this.t_campaigns, icon: 'campaign',  disabled: !this.campaigns},
                {key: 'elements', name: this.t_elements, icon: 'elements',  disabled: !this.elements},
                {key: 'widget', name: this.t_widgets, icon: 'widget',  disabled: this.widgetDisabled}
            ];
            let filteredTabs = tabs.filter(tab => {return tab.disabled === false;});
            this.showDndSelector = filteredTabs.length > 1;
            return filteredTabs;
        },
        selectedLayers() {
            /* Dependencies must be accessed to be tracked for changes,
             * the if statements would prevent access of all dependencies.
             * https://stackoverflow.com/a/51699388/1328511 */

            const activeLayerList = this.activeLayerList;
            const allowedLayerList = this.allowedLayerList;
            const selectedLayerIds = this.selectedLayerIds;
            const fixedLayersFilter = this.fixedLayersFilter;
            const selectedLayerNr = this.selectedLayerNr;

            if (selectedLayerIds.length) { // -> in template and campaign creator
                const allLayerIds = activeLayerList.map(layer => layer.value.toString());
                return selectedLayerIds.filter(layerID => allLayerIds.includes(layerID) && (fixedLayersFilter.includes(layerID) || !fixedLayersFilter.length));
            } else if (selectedLayerNr != null) { //-> only on publish page
                if (allowedLayerList[selectedLayerNr]) {
                    return [allowedLayerList[selectedLayerNr].value.toString()];
                } else return [];
            } else {
                return fixedLayersFilter;
            }
        },
        filteredWidgetsList() {
            return this.filterElementListForKeyword(this.widgetsList, this.search_widgets);
        },
        sortedWidgetsList() {
            /* Shallow copy array with 'slice' to create new instance, 'sort'
             * modifies the array in-place and would not work properly on a
             * computed property. This is also much more readable. */
            return this.sortWidgetsList(this.filteredWidgetsList.slice());
        },
        localStorageKeyDndSortOptionWidgets() {
            return `dnd-sort-widgets-${Laravel.activeProject}`;
        },
        setThumbnailHeightStyle() {
            // Controls the height of the thumbnails/tiles depending on weather 2cols or 3cols are chosen.
            return {
                '--thumbnail-hover-zoom-height': this.grid === '2cols' ? '121px' : '136px',
            };
        },
    },
    watch: {
        startActive: function () {
            this.active = this.startActive;
        },
        sort_option_widgets: function (val) {
            localStorage.setItem(this.localStorageKeyDndSortOptionWidgets, val);
        },
        disabled: function (val) {
            if (val === false) {
                this.loadTabNumber();
            }
        },
        tab_number(val) {
            localStorage.setItem(LSKeyCurrentTab, val);
        },
        mode: function () {
            this.$nextTick(() => {
                this.processWindowSize();
            });
            this.timeoutHeightRecalculation();
        },
        grid(newVal) {
            this.$nextTick(() => {
                this.processWindowSize();
            });

            if (newVal === '2cols') this.dragdropCollapsed = false;

            this.timeoutHeightRecalculation();
        },
        dragdropCollapsed() {
            this.$emit('dragdrop-collapsed');
            EaseEventBus.fire('WORKAROUND_recalculate_settings_height');
            this.timeoutHeightRecalculation();
        }
    },
    methods: {
        timeoutHeightRecalculation(forceRecalculation) {
            setTimeout(() => {
                // the same for all but elements
                const defaultHeight = this.calculateHeight('mediapool', forceRecalculation);
                const elementsHeight = this.calculateHeight('elements', forceRecalculation);
                const filterHeight = this.heightCalculator.getMemorizedHeights().filter;

                this.calculatedHeights = {
                    default: defaultHeight,
                    elements: elementsHeight,
                    defaultListHeight: defaultHeight - filterHeight,
                    elementsListHeight: elementsHeight
                }
            }, TIMEOUT_DURATION);
        },
        collapseSettings() {
            EaseEventBus.fire('collapseSettings');
        },
        handleSelectedTabChange(key) {
            this.collapseSettings();
            this.tab_number = this.elementTypes.findIndex(tab => tab.key === key);
            this.blockActivate();
            if(this.dragdropCollapsed) this.dragdropCollapsed = false;
        },
        processSelectedUsergroups: function () {
            let selectedUsergroups = [];
            if (this.selectedUsergroupIds.length > 0) { // -> in template and campaign creator
                selectedUsergroups = this.selectedUsergroupIds.filter(usergroupID => this.fixedUsergroupsFilter.includes(usergroupID) || this.fixedUsergroupsFilter.length === 0);
            } else {
                selectedUsergroups = this.fixedUsergroupsFilter;
            }

            let mergedUsergroups = [];

            _.each(selectedUsergroups, (selectedUsergroup) => {
                let current_index = this.fullUsergroupList.data.findIndex((usergroup) => {
                    return usergroup.value.toString() === selectedUsergroup;
                });
                let startLevel = current_index >= 0 ? this.fullUsergroupList.data[current_index].level : 0;
                let lowestLevel = startLevel + 1;

                let hierarchyUsergroups = [];
                while (current_index >= 0 && current_index < this.fullUsergroupList.data.length) {

                    if (this.fullUsergroupList.hierarchy === 'down') {
                        hierarchyUsergroups.push(this.fullUsergroupList.data[current_index].value.toString());

                        current_index++;
                        if (current_index >= this.fullUsergroupList.data.length || this.fullUsergroupList.data[current_index].level <= startLevel) {
                            break;
                        }
                    } else {
                        if (this.fullUsergroupList.data[current_index].level < lowestLevel) {
                            hierarchyUsergroups.push(this.fullUsergroupList.data[current_index].value.toString());
                            lowestLevel = this.fullUsergroupList.data[current_index].level;
                        }
                        if (current_index === 0 || this.fullUsergroupList.data[current_index].level === 0) {
                            break;
                        }
                        current_index--;
                    }
                }

                mergedUsergroups.push(hierarchyUsergroups);
            });

            this.selectedUsergroupsHierarchyConsidered = mergedUsergroups;
        },
        sortWidgetsList(listReference) {
            switch (this.sort_option_widgets) {
                case 0:
                    listReference.sort((a, b) => a.name.localeCompare(b.name));
                    break;
                case 1:
                    listReference.sort((a, b) => b.name.localeCompare(a.name));
                    break;
                case 2:
                    listReference.sort((a, b) => b.updated_at_millis - a.updated_at_millis);
                    break;
                case 3:
                    listReference.sort((a, b) => a.updated_at_millis - b.updated_at_millis);
                    break;
            }

            return listReference;
        },
        calculateHeight(tabName, forceRecalculation) {
            this.heightCalculator.memorizeHeights(forceRecalculation);
            const config = {
                mode: this.mode,
                grid: this.grid,
                tabName,
                collapsed: this.settingsCollapsed,
            };

            this.heightCalculator.updateConfig(config);
            const height = this.heightCalculator.execute('dragdrop');

            // console.log(`Height of DragDrop for ${tabName} recalculated to ${height}`);

            return height;
        },
        blockActivate: function () {
            this.blockActiveButton = true;
        },
        toggleCollapse: function () {
            this.dragdropCollapsed = !this.dragdropCollapsed;
            this.blockActiveButton = true;
        },
        setActive: function () {
            if (this.blockActiveButton) {
                this.blockActiveButton = false;
                return;
            }
            if (!this.setActiveBlocked) {
                this.setActiveBlocked = true;
                this.active = true;

                EaseEventBus.fire('dragdropActive');
                this.setActiveBlocked = false;
            }

        },
        loadUserLayers: function () {
            axios.post('/administration/user/loadlayers', {
                id: Laravel.activeProject,
                user: Laravel.activeUser
            }).then((response) => {
                this.allowedLayerList = response.data.allowedLayers;
                this.activeLayerList = response.data.activeLayers;
            }).catch((error) => {
                if (error.response?.status === 401) {
                    window.location = '/login';
                }

                if(++this.loadTriesLayers >= 3) {
                    toaster.error(this.t_load_layers_failed);
                } else {
                    setTimeout(this.loadUserLayers, 350)
                }
            });
        },
        processWindowSize: function () {
            let content = this.$refs.content;
            let elementsPerRowClass = 'three-in-a-row';

            if (content !== undefined) {
                let width = content.getBoundingClientRect().width;

                if (width > 900) {
                    elementsPerRowClass = 'five-in-a-row';
                } else if (width > 700) {
                    elementsPerRowClass = 'four-in-a-row';
                } else if (width > 500) {
                    elementsPerRowClass = 'three-in-a-row';
                } else if (width > 320) {
                    elementsPerRowClass = 'two-in-a-row';
                } else {
                    elementsPerRowClass = 'one-in-a-row'
                }
            }

            this.elementsPerRowClass = elementsPerRowClass;
        },
        loadUsergroups: function () {
            axios.post('/content/mediapool/loadusergroups', {
                id: Laravel.activeProject
            }).then((response) => {
                this.fullUsergroupList.data = response.data.data;
                this.fullUsergroupList.hierarchy = response.data.hierarchy;
                this.processSelectedUsergroups();
            }).catch((error) => {
                if (error.response?.status === 401) {
                    window.location = '/login';
                }
                if(++this.loadTriesUsergroups >= 3) {
                    toaster.error(this.t_load_usergroups_failed);
                } else {
                    setTimeout(this.loadUsergroups, 350);
                }
            });
        },
        fetchWidgets() {
            if (this.widgets === true) {
                this.loadingWidgets = true;
                axios.post('/administration/widget/get', {
                    id: Laravel.activeProject,
                }).then((response) => {
                    this.widgetsList = response.data.data.widgets.map(element => {
                        element.updated_at_millis = validityDateToJsDate(element.updated_at).getTime();
                        element.name_lowercase = element.name?.toLowerCase() || '';
                        return element;
                    });

                    this.$nextTick(() => {
                        EaseEventBus.fire('reinitializeDragula', this.disableCanvas);
                    });
                }).catch((error) => {
                    if (error.response?.status === 401) {
                        window.location = '/login';
                    }

                    if(++this.loadTriesWidgets >= 3) {
                        toaster.error(this.t_load_widgets_failed);
                    } else {
                        setTimeout(this.fetchWidgets, 350);
                    }
                }).finally(() => {
                    this.loadingWidgets = false;
                    this.loadTabNumber();
                });
            } else {
                this.loadTabNumber();
            }
        },
        setDragOverlay: function () {
            this.drag_drop_values.top = this.$refs.container.getBoundingClientRect().top - 1;
            this.drag_drop_values.left = this.$refs.container.getBoundingClientRect().left - 1;
            this.drag_drop_values.height = this.$refs.content.getBoundingClientRect().height;
            this.drag_drop_values.width = this.$refs.tab.getBoundingClientRect().width;
        },
        loadTabNumber: function () {
            this.tab_number = +localStorage.getItem(LSKeyCurrentTab);
            if (this.tab_number === null || this.tab_number > (this.elementTypes.length -1)) {
                this.tab_number = 0;
            }
        },
        /**
         * filter callback function to determine whether element is visible
         * */
        isElementVisible(element) {
            /* Dependencies must be accessed to be tracked for changes,
             * the if statements would prevent access of all dependencies.
             * https://stackoverflow.com/a/51699388/1328511
             * (this method is called as a dependency of a computed getter) */

            const selectedLayers = this.selectedLayers;
            const selectedUsergroupsHierarchyConsidered = this.selectedUsergroupsHierarchyConsidered;
            const selectedUsergroupsStrict = this.selectedUsergroupsStrict;

            let visibleByLayer = true;
            let visibleByUsergroup = true;

            if (selectedLayers.length && element.layers.length) {
                let elementLayers = Object.keys(element.layers.filter(layer => layer.active)).map(f => element.layers[f].id.toString());
                if (!selectedLayers.every(layer => elementLayers.includes(layer))) {
                    visibleByLayer = false;
                }
            }

            if (visibleByLayer) {
                if (selectedUsergroupsHierarchyConsidered.length && (element.usergroups.length || selectedUsergroupsStrict)) {
                    let elementUsergroups = element.usergroups.map(usergroup => usergroup.id.toString());
                    if (selectedUsergroupsStrict) {
                        visibleByUsergroup = selectedUsergroupsHierarchyConsidered.every((hierarchy_usergroups) => hierarchy_usergroups.some(usergroup => elementUsergroups.includes(usergroup)));
                    } else {
                        visibleByUsergroup = selectedUsergroupsHierarchyConsidered.some((hierarchy_usergroups) => hierarchy_usergroups.some(usergroup => elementUsergroups.includes(usergroup)));
                    }
                }

                if (visibleByUsergroup) {
                    return true;
                }
            }

            return false;
        },
        filterElementListForKeyword(elementList, keyword) {
            if(keyword) {
                return elementList.filter(element => element.name_lowercase.includes(keyword));
            } else return elementList;
        },
    },
    created() {
        this.sort_option_widgets = +(localStorage.getItem(this.localStorageKeyDndSortOptionWidgets) ?? 2);
    },
    mounted() {
        this.loadUserLayers();
        this.loadUsergroups();

        this.fetchWidgets();

        this.dragdropCollapsed = this.startCollapsed;

        EaseEventBus.listen('frameInfos', (data) => {
            this.editorFrame = _.extend({}, this.editorFrame, data);
        });
        EaseEventBus.listen('layoutInfo', (data) => {
            this.layoutInfo = _.extend({}, this.layoutInfo, data);
        });

        EaseEventBus.listen('settingsActive', () => {
            this.active = false;
        });
        EaseEventBus.listen('settingsCollapsed', (is_collapsed) => {
            this.settingsCollapsed = is_collapsed;
        });

        EaseEventBus.listen('layerSelected', (layer) => {
            this.selectedLayerNr = layer?.toString() ?? null;
        });
        EaseEventBus.listen('layersSelectionChanged', (data) => {
            this.selectedLayerIds = data;
        });
        EaseEventBus.listen('usergroupsSelectionChanged', (data) => {
            this.selectedUsergroupIds = data;
            this.processSelectedUsergroups();
        });
        EaseEventBus.listen('usergroupsStrictChanged', (data) => {
            this.selectedUsergroupsStrict = data;
        });

        if (this.fixed_layers_filter) {
            this.fixedLayersFilter = JSON.parse(this.fixed_layers_filter);
        }
        if (this.fixed_usergroups_filter) {
            this.fixedUsergroupsFilter = JSON.parse(this.fixed_usergroups_filter);
            this.processSelectedUsergroups();
        }

        this.setDragOverlay();
        this.processWindowSize();
        this.$nextTick(() => {
            window.addEventListener('resize', this.processWindowSize);
        });

        EaseEventBus.listen('windowResize', (event) => {
            this.timeoutHeightRecalculation(true);
        });

        EaseEventBus.listen('WORKAROUND_recalculate_dragdrop_height', () => {
            this.timeoutHeightRecalculation();
        });

        this.timeoutHeightRecalculation();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.processWindowSize);
    }
}
</script>
